import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useProductMargins } from "@/hooks/useProductMargins";
import { useRestaurant } from "@/hooks/useRestaurant";
import { exportMargensToPDF, exportMargensToExcel } from "@/lib/report-export";
import { AlertTriangle, TrendingUp, Package, Percent, Download } from "lucide-react";

export default function MargensPage() {
  const { products, lowMarginProducts, isLoading, lowMarginAlertPercentage } =
    useProductMargins();
  const { activeRestaurant } = useRestaurant();

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  const handleExportPDF = () => {
    exportMargensToPDF(products, lowMarginProducts, lowMarginAlertPercentage, activeRestaurant?.name);
  };

  const handleExportExcel = () => {
    exportMargensToExcel(products, lowMarginProducts, lowMarginAlertPercentage);
  };

  const averageMargin =
    products.filter((p) => p.hasRecipe).length > 0
      ? products
          .filter((p) => p.hasRecipe)
          .reduce((sum, p) => sum + p.marginPercent, 0) /
        products.filter((p) => p.hasRecipe).length
      : 0;

  const totalRevenue = products.reduce((sum, p) => sum + p.price, 0);
  const totalCost = products.reduce((sum, p) => sum + p.cost, 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Margem por Produto</h1>
          <p className="text-muted-foreground">
            Análise de custos e margens de lucro
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            Limite de alerta: {lowMarginAlertPercentage}%
          </Badge>
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <Download className="h-4 w-4 mr-1" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <Download className="h-4 w-4 mr-1" />
            Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Produtos</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{products.length}</div>
            <p className="text-xs text-muted-foreground">
              {products.filter((p) => p.hasRecipe).length} com receita
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Margem Média</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageMargin.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              Produtos com receita
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custo Total</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalCost)}</div>
            <p className="text-xs text-muted-foreground">
              Soma dos custos unitários
            </p>
          </CardContent>
        </Card>

        <Card className={lowMarginProducts.length > 0 ? "border-destructive" : ""}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Margem Baixa</CardTitle>
            <AlertTriangle
              className={`h-4 w-4 ${
                lowMarginProducts.length > 0
                  ? "text-destructive"
                  : "text-muted-foreground"
              }`}
            />
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                lowMarginProducts.length > 0 ? "text-destructive" : ""
              }`}
            >
              {lowMarginProducts.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Abaixo de {lowMarginAlertPercentage}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Low Margin Products Alert */}
      {lowMarginProducts.length > 0 && (
        <Card className="border-destructive bg-destructive/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Produtos com Margem Abaixo do Limite
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Custo</TableHead>
                  <TableHead className="text-right">Preço</TableHead>
                  <TableHead className="text-right">Margem R$</TableHead>
                  <TableHead className="text-right">Margem %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lowMarginProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell>{product.categoryName || "-"}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(product.cost)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(product.price)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(product.marginValue)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="destructive">
                        {product.marginPercent.toFixed(1)}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* All Products Table */}
      <Card>
        <CardHeader>
          <CardTitle>Todos os Produtos</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produto</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Receita</TableHead>
                <TableHead className="text-right">Custo</TableHead>
                <TableHead className="text-right">Preço</TableHead>
                <TableHead className="text-right">Margem R$</TableHead>
                <TableHead className="text-right">Margem %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow
                  key={product.id}
                  className={
                    product.hasRecipe && product.marginPercent < lowMarginAlertPercentage
                      ? "bg-destructive/5"
                      : ""
                  }
                >
                  <TableCell className="font-medium">{product.name}</TableCell>
                  <TableCell>{product.categoryName || "-"}</TableCell>
                  <TableCell>
                    {product.hasRecipe ? (
                      <Badge variant="secondary">Sim</Badge>
                    ) : (
                      <Badge variant="outline">Não</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {product.hasRecipe ? formatCurrency(product.cost) : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(product.price)}
                  </TableCell>
                  <TableCell className="text-right">
                    {product.hasRecipe ? formatCurrency(product.marginValue) : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    {product.hasRecipe ? (
                      <Badge
                        variant={
                          product.marginPercent < lowMarginAlertPercentage
                            ? "destructive"
                            : "default"
                        }
                      >
                        {product.marginPercent.toFixed(1)}%
                      </Badge>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
